import { shallowEqual } from "react-redux";
import { useCarConfigSelector } from "../redux/store";
import {
    getCurrentNavQuerySelector,
    pricingDetailsSelector,
    showCarConfigMonthlyRateSelector,
} from "../utils/selectorUtils";
import { CarConfigStep } from "../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { showLegalBannerDisclaimer } from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import type { LegalBannerDisclaimerDataType } from "../../../shared-logic/types/FinanceTypes";
import { LegalBannerTableOrientation } from "../../../shared-logic/types/FinanceTypes";
import { financeTableToObject } from "../../../shared-logic/utils/legalBannerDisclaimerUtils";
import type { FinanceInfoDisclaimerType } from "../../../../common-deprecated/types/CommonTypes";
import { useCarConfigOptionalLabel } from "../utils/labelUtils";

export type DisclaimerType = {
    index?: number;
    id: string;
    name?: string;
    from?: string;
    until?: string;
    disclaimer?: string;
};

export type DisclaimerContentReturnType = {
    items: DisclaimerType[];
    legalBannerDisclaimerData: LegalBannerDisclaimerDataType | null;
};

export const useDisclaimers = (): DisclaimerContentReturnType => {
    const disclaimerPromotions = useCarConfigSelector((state) => state.items.disclaimerPromotions);
    const promotions = useCarConfigSelector((state) => state.items.promotions);
    const financeDisclaimer = useCarConfigSelector((state) => state.finance.info.disclaimer);
    const financeOption = useCarConfigSelector((state) => state.utils.financeOption);
    const activeStep = useCarConfigSelector(getCurrentNavQuerySelector, shallowEqual)?.step;
    const { showFinanceDisclaimerInline } = useCarConfigSelector(pricingDetailsSelector, shallowEqual);
    const monthlyEnabled = useCarConfigSelector(showCarConfigMonthlyRateSelector, shallowEqual);
    const hasLegalBannerDisclaimer = useCarConfigSelector((state) => showLegalBannerDisclaimer(state.commonSettings));

    const [
        genericDisclaimerLabel,
        genericFinanceDisclaimerLabel,
        genericSpecsDisclaimerTitleLabel,
        genericSpecsDisclaimerLabel,
        genericHighlightsDisclaimerTitleLabel,
        genericHighlightsDisclaimerLabel,
    ] = useCarConfigOptionalLabel([
        "genericDisclaimer",
        "genericFinanceDisclaimer",
        "genericSpecsDisclaimerTitle",
        "genericSpecsDisclaimer",
        "genericHighlightsDisclaimerTitle",
        "genericHighlightsDisclaimer",
    ]);

    const disclaimers: DisclaimerType[] = disclaimerPromotions.map((promo) => ({
        index: promotions.find((promotion) => promotion.id === promo.id)?.index || 1,
        id: promo.id,
        name: promo.name,
        from: promo.from,
        until: promo.until,
        disclaimer: promo.disclaimer,
    }));

    // Check if the genericDisclaimer is available.
    if (genericDisclaimerLabel) {
        disclaimers.unshift({
            id: "genericDisclaimer",
            disclaimer: genericDisclaimerLabel,
        });
    }

    // Check if the generic finance disclaimer should be shown.
    if (genericFinanceDisclaimerLabel && financeOption === "monthly") {
        disclaimers.unshift({
            id: "genericFinanceDisclaimer",
            disclaimer: genericFinanceDisclaimerLabel,
        });
    }

    if (activeStep === CarConfigStep.Specs && genericSpecsDisclaimerLabel) {
        disclaimers.unshift({
            id: "genericSpecsDisclaimer",
            name: genericSpecsDisclaimerTitleLabel ?? undefined,
            disclaimer: genericSpecsDisclaimerLabel,
        });
    } else if (activeStep === CarConfigStep.Highlights && genericHighlightsDisclaimerLabel) {
        disclaimers.unshift({
            id: "genericHighlightsDisclaimer",
            name: genericHighlightsDisclaimerTitleLabel ?? undefined,
            disclaimer: genericHighlightsDisclaimerLabel,
        });
    }

    if (
        monthlyEnabled &&
        !showFinanceDisclaimerInline &&
        financeDisclaimer &&
        financeDisclaimer.value &&
        !hasLegalBannerDisclaimer
    ) {
        disclaimers.push({
            id: "",
            disclaimer: financeDisclaimer.value,
        });
    }
    let legalBannerDisclaimerData = null;
    if (hasLegalBannerDisclaimer) {
        // Customize and onward only need to show one value
        if (financeDisclaimer?.table) {
            legalBannerDisclaimerData = {
                finance: [financeTableToObject(financeDisclaimer.table)],
                minMax: {
                    min: financeDisclaimer.table,
                },
                disclaimer: financeDisclaimer.value,
            };
        }
    }

    return { items: disclaimers, legalBannerDisclaimerData };
};

type FinanceModalDisclaimerContentReturnType = {
    disclaimer?: string;
    disclaimerTable?: FinanceInfoDisclaimerType["table"];
    legalBannerDisclaimerData: LegalBannerDisclaimerDataType | null;
};

export const useFinanceModalDisclaimers = (): FinanceModalDisclaimerContentReturnType => {
    const disclaimer = useCarConfigSelector((state) => state.finance.info.disclaimer?.value);
    const disclaimerTable = useCarConfigSelector((state) => state.finance.info.disclaimer?.table);
    const hasLegalBannerDisclaimer = useCarConfigSelector((state) => showLegalBannerDisclaimer(state.commonSettings));

    const legalBannerDisclaimer =
        hasLegalBannerDisclaimer && disclaimerTable
            ? {
                  finance: [financeTableToObject(disclaimerTable)],
                  minMax: {
                      min: disclaimerTable,
                  },
                  disclaimer: disclaimer || "",
                  tableOrientation: LegalBannerTableOrientation.vertical,
              }
            : null;

    return { disclaimer, disclaimerTable, legalBannerDisclaimerData: legalBannerDisclaimer };
};
